/**
 * Organization value lists shared by the API (`organizations.kind`,
 * `organization_members.role` / `.status`, the member access state), the wire
 * contract (`@kiao-voice/api-contract/organization`) and the web / desktop team
 * pages.
 *
 * `organizations.kind` and both membership columns carry a DB CHECK; the
 * migrations keep their own frozen copies, and
 * `apps/api/tests/unit/value_list_check_constraints.spec.ts` asserts the live
 * CHECK set equals these constants.
 */

/**
 * The API's 403 `{ error }` for an `X-Active-Context` the caller can no longer
 * use. Clients treat it as self-healing: the stored context is cleared and the
 * next request omits the header, so it is never a reason to sign out.
 */
export const ACTIVE_CONTEXT_FORBIDDEN_ERROR = 'active_context_forbidden'

/**
 * Whether an org is the owner's PERSONAL workspace (personal subscription and
 * credit pool) or a TEAM they own (team subscription, members, team pool).
 */
export const ORGANIZATION_KINDS = ['personal', 'team'] as const
export type OrganizationKind = (typeof ORGANIZATION_KINDS)[number]

export const ORGANIZATION_MEMBER_ROLES = ['owner', 'admin', 'member'] as const
export type OrganizationMemberRole = (typeof ORGANIZATION_MEMBER_ROLES)[number]

export const ORGANIZATION_MEMBER_STATUSES = ['invited', 'active', 'revoked'] as const
export type OrganizationMemberStatus = (typeof ORGANIZATION_MEMBER_STATUSES)[number]

/**
 * Whether a membership may use the team, independent of `status`: an invited
 * or active member can be `disabled`, which keeps the membership but takes no
 * seat. Derived from `organization_members.disabled_at`, not a stored column.
 */
export const ORGANIZATION_MEMBER_ACCESS_STATES = ['active', 'disabled'] as const
export type OrganizationMemberAccessState = (typeof ORGANIZATION_MEMBER_ACCESS_STATES)[number]

/** The owner's roster action on a member's access state (web and desktop team pages). */
export const ORGANIZATION_MEMBER_ACCESS_ACTIONS = ['disable', 'enable'] as const
export type OrganizationMemberAccessAction = (typeof ORGANIZATION_MEMBER_ACCESS_ACTIONS)[number]

/**
 * The caller's role in an `ActiveContext` / `SwitchableContext`: whether they
 * own this workspace or are a member of it. Narrower than
 * `ORGANIZATION_MEMBER_ROLES` on purpose — `admin` isn't a context-switching
 * distinction, only a roster permission tier.
 */
export const ACTIVE_CONTEXT_ROLES = ['owner', 'member'] as const
export type ActiveContextRole = (typeof ACTIVE_CONTEXT_ROLES)[number]
